import { Link, router } from '@inertiajs/react'
import { ArrowLeft, LogOut, Mail, Phone, Building, User, Wrench, Clock, BarChart3 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

interface Inquiry {
  id: number
  name: string
  email: string
  company: string
  phone: string
  assets: string | null
  created_at: string
}

export default function Index({ inquiries }: { inquiries: Inquiry[] }) {
  return (
    <div className="min-h-screen bg-brand-light font-montserrat text-brand-dark">
      <header className="bg-brand-dark py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <img
                src="/curbtek-logo.png"
                alt="CurbTek Logo"
                className="h-10 object-contain"
              />
            </div>
            <div className="flex items-center gap-6">
              <Link
                href="/admin/visits"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <BarChart3 className="h-4 w-4" />
                Analytics
              </Link>
              <Link
                href="/admin"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Admin Home
              </Link>
              <button
                onClick={() => router.delete('/session')}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Log Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="border-brand-light shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-brand-dark">
              Inquiries
            </CardTitle>
            <CardDescription className="text-brand-gray">
              {inquiries.length} {inquiries.length === 1 ? 'inquiry' : 'inquiries'} received
            </CardDescription>
          </CardHeader>
          <CardContent>
            {inquiries.length === 0 ? (
              <p className="text-brand-gray text-center py-12">
                No inquiries yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-light">
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" /> Name
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" /> Email
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Building className="h-3.5 w-3.5" /> Company
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" /> Phone
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Wrench className="h-3.5 w-3.5" /> Assets
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-brand-gray">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" /> Submitted
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inquiry) => (
                      <tr
                        key={inquiry.id}
                        className="border-b border-brand-light last:border-0 hover:bg-brand-light/50 transition-colors"
                      >
                        <td className="py-3 px-4 font-medium text-brand-dark">
                          {inquiry.name}
                        </td>
                        <td className="py-3 px-4 text-brand-gray">
                          {inquiry.email}
                        </td>
                        <td className="py-3 px-4 text-brand-gray">
                          {inquiry.company}
                        </td>
                        <td className="py-3 px-4 text-brand-gray">
                          {inquiry.phone}
                        </td>
                        <td className="py-3 px-4 text-brand-gray">
                          {inquiry.assets ?? '—'}
                        </td>
                        <td className="py-3 px-4 text-brand-gray">
                          {new Date(inquiry.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
